import * as THREE from 'three';
import { Entity } from './Entity.js';
import type { CollisionShape } from '../systems/Collision.js';

/**
 * Exit door entity that locks/unlocks based on room clear status.
 *
 * Visual states:
 * - Locked: Red/dark color, represents blocked exit
 * - Unlocked: Green/bright color, represents open exit
 *
 * The door is interactive - players can advance to the next room
 * when they interact with an unlocked door.
 */
export class ExitDoor extends Entity {
  private locked: boolean = true;
  private readonly size: number = 1.5;
  private material: THREE.MeshStandardMaterial;

  constructor(x: number, y: number) {
    super(x, y, 0);

    // Create door mesh
    this.createMesh();
  }

  /**
   * Create the visual representation of the door.
   */
  private createMesh(): void {
    const group = new THREE.Group();

    // Door frame (always dark gray)
    const frameGeometry = new THREE.BoxGeometry(this.size * 1.2, 0.3, this.size * 1.2);
    const frameMaterial = new THREE.MeshStandardMaterial({ color: 0x333333 });
    const frame = new THREE.Mesh(frameGeometry, frameMaterial);
    frame.position.y = 0.15;
    group.add(frame);

    // Door panel (changes color based on locked state)
    const panelGeometry = new THREE.BoxGeometry(this.size, 0.5, this.size);
    this.material = new THREE.MeshStandardMaterial({
      color: 0x880000, // Dark red when locked
      emissive: 0x440000,
      emissiveIntensity: 0.3,
    });
    const panel = new THREE.Mesh(panelGeometry, this.material);
    panel.position.y = 0.4;
    group.add(panel);

    // Add a glowing indicator on top
    const indicatorGeometry = new THREE.CylinderGeometry(0.2, 0.2, 0.1, 16);
    const indicatorMaterial = new THREE.MeshStandardMaterial({
      color: 0xff0000, // Red when locked
      emissive: 0xff0000,
      emissiveIntensity: 0.8,
    });
    const indicator = new THREE.Mesh(indicatorGeometry, indicatorMaterial);
    indicator.position.y = 0.7;
    group.add(indicator);

    this.mesh = group;
    this.updateMeshTransform();
  }

  /**
   * Unlock the door, allowing passage to the next room.
   */
  unlock(): void {
    if (!this.locked) return;

    this.locked = false;
    this.updateVisuals();
  }

  /**
   * Lock the door, preventing passage.
   */
  lock(): void {
    if (this.locked) return;

    this.locked = true;
    this.updateVisuals();
  }

  /**
   * Check if the door is unlocked.
   */
  isUnlocked(): boolean {
    return !this.locked;
  }

  /**
   * Update the visual appearance based on locked state.
   */
  private updateVisuals(): void {
    if (!this.mesh) return;

    const group = this.mesh as THREE.Group;
    const panel = group.children[1] as THREE.Mesh;
    const indicator = group.children[2] as THREE.Mesh;

    if (this.locked) {
      // Locked: Dark red
      (panel.material as THREE.MeshStandardMaterial).color.setHex(0x880000);
      (panel.material as THREE.MeshStandardMaterial).emissive.setHex(0x440000);
      (panel.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.3;

      (indicator.material as THREE.MeshStandardMaterial).color.setHex(0xff0000);
      (indicator.material as THREE.MeshStandardMaterial).emissive.setHex(0xff0000);
      (indicator.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.8;
    } else {
      // Unlocked: Bright green
      (panel.material as THREE.MeshStandardMaterial).color.setHex(0x00aa00);
      (panel.material as THREE.MeshStandardMaterial).emissive.setHex(0x00ff00);
      (panel.material as THREE.MeshStandardMaterial).emissiveIntensity = 0.5;

      (indicator.material as THREE.MeshStandardMaterial).color.setHex(0x00ff00);
      (indicator.material as THREE.MeshStandardMaterial).emissive.setHex(0x00ff00);
      (indicator.material as THREE.MeshStandardMaterial).emissiveIntensity = 1.0;
    }
  }

  /**
   * Get collision shape for interaction detection.
   */
  getCollisionShape(): CollisionShape {
    return {
      type: 'aabb',
      bounds: {
        minX: this.position.x - this.size / 2,
        maxX: this.position.x + this.size / 2,
        minY: this.position.y - this.size / 2,
        maxY: this.position.y + this.size / 2,
      },
    };
  }

  /**
   * Update method (door is static, no behavior needed).
   */
  update(dt: number): void {
    // Static entity - no update logic needed
  }

  /**
   * Clean up resources.
   */
  destroy(): void {
    if (this.mesh) {
      this.material.dispose();
    }
    super.destroy();
  }
}
